import { useEffect, useState } from "react";
import DashboardLayout from "../../../layoutcontainers/DashboardLayout";
import Table from "../../../components/Table";
import MaterialTypography from "../../../components/MaterialTypography";
import { Card, CardHeader, Stack } from "@mui/material";
import { useNavigate } from "react-router-dom";
import MaterialButton from "../../../components/MaterialButton";
import AddIcon from "@mui/icons-material/Add";
import { FileUpload, FilterAlt } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { deleteData, fetchData } from "../../../services";
import {
  setCurrentPage,
  setRowsPerPage,
  setTotalPages,
} from "./store/motorRTOStore";
import { useLoading } from "../../../components/Loading";
import BulkUpload from "../../../components/blukUplod";
import { useSnackbar } from "../../../context/popup";
import RtoListFilter from "./components/RtoListFilter.js";

const MotorRTOList = () => {

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { setCircleLoading } = useLoading();
  const { showSnackbar } = useSnackbar();

  const currentPage = useSelector((state) => state?.motorRTOStore?.currentPage);
  const rowsPerPage = useSelector((state) => state?.motorRTOStore?.rowsPerPage);
  const totalPages = useSelector((state) => state?.motorRTOStore?.totalPages);

  const [openUploadFileDialog, setOpenUploadFileDialog] = useState(false);
  const [search, setSearch] = useState("");
  const [isRefresh, setIsRefresh] = useState(false);
  const [rtoData, setRTOData] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const [filterData, setFilterData] = useState({
    cityId: "",
    rtoRegionId: "",
    rtoZoneId: "",
    isActive: "",
  });

  const fetchRTOData = async () => {
    setCircleLoading(true);
    try {
      let url = `/reference/motor/rto?page=${currentPage}&limit=${rowsPerPage}&searchTerm=${search}&cityId=${filterData.cityId}&rtoRegionId=${filterData?.rtoRegionId}&rtoZoneId=${filterData?.rtoZoneId}`;

      //for active and inactive status
      if (filterData.isActive !== "") {
        url += `&isActive=${filterData.isActive}`;
      }

      const response = await fetchData(url);
      if (response?.success) {
        setRTOData(response?.data?.items);
        dispatch(setTotalPages(response?.data?.totalPages));
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      showSnackbar(error?.response?.data?.message || 'Network Error', "error");
    } finally {
      setCircleLoading(false);
    }
  };

  useEffect(() => {
    fetchRTOData();
  }, [isRefresh, currentPage, rowsPerPage, search]);

  const columns = [
    {
      key: "sno",
      label: "S.No",
      renderCell: (row) => row.sno,
    },
    {
      key: "name",
      label: "RTO Name",
      renderCell: (row) => row.name,
    },
    {
      key: "displayName",
      label: "Display Name",
      renderCell: (row) => row.displayName,
    },
    {
      key: "city",
      label: "City",
      renderCell: (row) => row.city?.name,
    },
    {
      key: "state",
      label: "State",
      renderCell: (row) => row.city?.district?.state?.name,
    },
    {
      key: "createdAt",
      label: "Created At",
      renderCell: (row) => new Date(row.createdAt).toLocaleDateString('en-IN'),
    },
    {
      key: "isActive",
      label: "Status",
      renderCell: (row) => (
        <span>
          {row.isActive ? (
            <span style={{ color: "green", fontWeight: "bold" }}>Active</span>
          ) : (
            <span style={{ color: "red", fontWeight: "bold" }}>Inactive</span>
          )}
        </span>
      ),
    },
  ];

  const rows = rtoData.map((rto, index) => ({
    ...rto,
    sno: index + 1,
  }));

  const handlePaginationChange = (page) => {
    dispatch(setCurrentPage(page));
  };

  const handleRowsPerPageChange = (rowsPerPage) => {
    dispatch(setRowsPerPage(rowsPerPage));
    dispatch(setCurrentPage(1));
  };

  const handleEdit = (row) => {
    navigate(`/motor-master/edit-motorRTO/${row?.id}`);
  };

  const handleView = (row) => {
    navigate(`/motor-master/view-motorRTO/${row?.id}`);
  };

  const handleDelete = async (row) => {
    try {
      await deleteData(`motor-rto/${row?.id}`);
      setIsRefresh(!isRefresh);
    } catch (error) {
      console.error("Error deleting data:", error);
    }
  };

  const actions = {
    edit: true,
    view: true,
    delete: true,
    onEdit: (row) => handleEdit(row),
    onView: (row) => handleView(row),
    onDelete: (row) => handleDelete(row),
  };

  const handleAddNewRTO = () => {
    navigate("/motor-master/add-motorRTO");
  };

  const handleSearch = (value) => {
    setSearch(value);
  };
  return (
    <DashboardLayout>
      <CardHeader
        title={
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
          >
            <Stack direction="column" spacing={0}>
              <MaterialTypography variant="h5" fontWeight="medium">
                Motor RTO list
              </MaterialTypography>
              <MaterialTypography variant="caption" color="text">
                List of RTOs and its respective details.
              </MaterialTypography>
            </Stack>

            <Stack direction="row" spacing={1}>
              <MaterialButton
                variant="gradient"
                color="dark"
                size="small"
                startIcon={<FilterAlt />}
                onClick={(event) => setAnchorEl(event.currentTarget)}
              >
                Filter
              </MaterialButton>
              <MaterialButton
                variant="gradient"
                color="dark"
                size="small"
                startIcon={<FileUpload />}
                onClick={() => setOpenUploadFileDialog(true)}
              >
                Bulk Upload
              </MaterialButton>
              <MaterialButton
                variant="gradient"
                color="dark"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => handleAddNewRTO()}
              >
                Add RTO
              </MaterialButton>
            </Stack>
          </Stack>
        }
      />
      <Card sx={{ mx: 1 }}>
        <Table
          columns={columns}
          data={rows}
          actions={actions}
          pagination={{
            totalPages,
            currentPage,
            onPageChange: (page) => handlePaginationChange(page),
            rowsPerPage,
            onRowsPerPageChange: (rowsPerPage) =>
              handleRowsPerPageChange(rowsPerPage),
            rowsPerPageOptions: [5, 10, 15, 20],
          }}
          getRowKey={(row) => row.id}
          className="custom-table"
          noDataLabel={"No motor RTO data found"}
          handleSearch={handleSearch}
        />
      </Card>
      <BulkUpload
        open={openUploadFileDialog}
        setOpen={setOpenUploadFileDialog}
        endPoint="/reference/master/bulkUpload/motor-rto"
        fetchList={fetchRTOData}
      />
      <RtoListFilter
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        filterData={filterData}
        setFilterData={setFilterData}
        onApply={() => {
          setAnchorEl(null); // close popup
          fetchRTOData(); // call API after filter applied
        }}
      />
    </DashboardLayout>
  );
};

export default MotorRTOList;
